
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";
import { handleNewMessage } from "./utils/messageHandlers";

interface UseRealtimeMessagesProps {
  otherUserId: string;
  reagentId?: string;
  refreshMessages: () => void;
  onNewMessage?: () => void;
}

export function useRealtimeMessages({
  otherUserId,
  reagentId,
  refreshMessages,
  onNewMessage
}: UseRealtimeMessagesProps) {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const isMountedRef = useRef(true);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    if (!user || !otherUserId) return;

    // Clean up existing subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Create new subscription
    const channel = supabase
      .channel('messages-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `sender_id=eq.${otherUserId},recipient_id=eq.${user.id}`
        },
        (payload) => {
          if (isMountedRef.current) {
            handleNewMessage(
              payload,
              otherUserId,
              reagentId,
              onNewMessage,
              refreshMessages,
              isMountedRef.current
            );
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsSubscribed(true);
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          setIsSubscribed(false);
        }
      });

    channelRef.current = channel;

    return () => {
      isMountedRef.current = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setIsSubscribed(false);
    };
  }, [user?.id, otherUserId, reagentId, refreshMessages, onNewMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { isSubscribed };
}
